import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { localAdmin } from "./pending-orders.server";
import { sendMails } from "./email-smtp.server";
import {
  renderReviewPendingCustomerEmail,
  renderReviewAdminEmail,
} from "./review-email";
import {
  summarize,
  reviewRateLimit,
  checkVerifiedPurchase,
  type ReviewRow,
  type ReviewSummary,
} from "./reviews.server";

function clientIp(): string {
  try {
    const req = getRequest();
    return (
      req.headers.get("cf-connecting-ip") ||
      req.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ||
      "unknown"
    );
  } catch {
    return "unknown";
  }
}

export type PublicReview = {
  id: string;
  author_name: string;
  rating: number;
  title: string | null;
  body: string;
  verified_purchase: boolean;
  helpful_count: number;
  created_at: string;
};

export const listReviews = createServerFn({ method: "GET" })
  .inputValidator((input: { productId: string }) =>
    z.object({ productId: z.string().min(1).max(64) }).parse(input),
  )
  .handler(async ({ data }): Promise<{ reviews: PublicReview[]; summary: ReviewSummary }> => {
    const { data: rows, error } = await localAdmin
      .from("product_reviews")
      .select("id, author_name, rating, title, body, verified_purchase, helpful_count, created_at")
      .eq("product_id", data.productId)
      .eq("status", "approved")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) {
      console.error("listReviews failed", error);
      return { reviews: [], summary: { count: 0, average: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 } } };
    }
    const list = (rows ?? []) as ReviewRow[];
    const reviews: PublicReview[] = list.map((r) => ({
      id: r.id,
      author_name: r.author_name,
      rating: r.rating,
      title: r.title,
      body: r.body,
      verified_purchase: r.verified_purchase,
      helpful_count: r.helpful_count,
      created_at: r.created_at,
    }));
    return { reviews, summary: summarize(list) };
  });

/** Σύνοψη αξιολογήσεων ανά προϊόν (για τα αστεράκια στις λίστες προϊόντων). */
export type ReviewCardSummary = { productId: string; count: number; average: number };

export const listReviewSummaries = createServerFn({ method: "GET" }).handler(
  async (): Promise<{ items: ReviewCardSummary[] }> => {
    const { data: rows, error } = await localAdmin
      .from("product_reviews")
      .select("product_id, rating")
      .eq("status", "approved")
      .limit(5000);
    if (error) {
      console.error("listReviewSummaries failed", error);
      return { items: [] };
    }
    const agg = new Map<string, { sum: number; count: number }>();
    for (const r of (rows ?? []) as { product_id: string; rating: number }[]) {
      if (!r.product_id) continue;
      const cur = agg.get(r.product_id) ?? { sum: 0, count: 0 };
      cur.sum += r.rating;
      cur.count += 1;
      agg.set(r.product_id, cur);
    }
    const items: ReviewCardSummary[] = [];
    for (const [productId, v] of agg) {
      items.push({
        productId,
        count: v.count,
        average: Math.round((v.sum / v.count) * 10) / 10,
      });
    }
    return { items };
  },
);

const submitSchema = z.object({
  productId: z.string().min(1).max(64),
  productPsId: z.number().int().positive().optional(),
  authorName: z.string().trim().min(2).max(120),
  rating: z.number().int().min(1).max(5),
  title: z.string().trim().max(160).optional(),
  body: z.string().trim().max(4000).optional().default(""),
  email: z.string().trim().email().max(200),
});

export const submitReview = createServerFn({ method: "POST" })
  .inputValidator((input: z.input<typeof submitSchema>) => submitSchema.parse(input))
  .handler(async ({ data }): Promise<{ ok: boolean; status: "approved" | "pending"; error?: string }> => {
    const ip = clientIp();
    const allowed = await reviewRateLimit(ip, { max: 5, windowMinutes: 60 });
    if (!allowed) {
      return { ok: false, status: "pending", error: "Πολλές αξιολογήσεις σε σύντομο διάστημα. Δοκίμασε αργότερα." };
    }

    const verified = await checkVerifiedPurchase(data.email, data.productId);

    // Reviews are NOT published automatically. They are stored as "pending"
    // and must be approved manually from the MrPanda OSP before they appear.
    const { data: inserted, error } = await localAdmin
      .from("product_reviews")
      .insert({
        product_id: data.productId,
        product_ps_id: data.productPsId ?? null,
        author_name: data.authorName,
        rating: data.rating,
        title: data.title?.trim() ? data.title.trim() : null,
        body: data.body ?? "",
        email: data.email,
        verified_purchase: verified,
        status: "pending",
      })
      .select("id, product_id, product_ps_id, author_name, rating, title, body, verified_purchase, created_at")
      .maybeSingle();
    if (error) {
      console.error("submitReview insert failed", error);
      return { ok: false, status: "pending", error: "Δεν ήταν δυνατή η αποθήκευση της αξιολόγησης." };
    }

    // Notify OSP (fire-and-forget) that a new review is awaiting moderation.
    if (inserted) {
      const row = inserted as {
        id: string;
        product_id: string;
        product_ps_id: number | null;
        author_name: string;
        rating: number;
        title: string | null;
        body: string;
        verified_purchase: boolean;
        created_at: string;
      };
      const { notifyOpsReviewCreated } = await import("./notify-ops.functions");
      notifyOpsReviewCreated({
        id: row.id,
        product_id: row.product_id,
        product_ps_id: row.product_ps_id ?? undefined,
        author_name: row.author_name,
        rating: row.rating,
        title: row.title ?? undefined,
        body: row.body,
        verified_purchase: row.verified_purchase,
        created_at: row.created_at,
      }).catch((e: unknown) => console.error("notifyOpsReviewCreated failed", e));

      // Branded emails (fire-and-forget): confirmation to the customer that the
      // review is awaiting approval + notification to the shop (info@mrpanda.gr).
      const emailData = {
        authorName: row.author_name,
        email: data.email,
        rating: row.rating,
        title: row.title,
        body: row.body,
        productId: row.product_id,
        verifiedPurchase: row.verified_purchase,
      };
      sendMails([
        {
          to: data.email,
          reply: "info@mrpanda.gr",
          subject: "Λάβαμε την αξιολόγησή σου! 🐼 — MrPanda",
          html: renderReviewPendingCustomerEmail(emailData),
        },
        {
          to: "info@mrpanda.gr",
          reply: data.email,
          subject: `Νέα αξιολόγηση (${row.rating}★) από ${row.author_name}`,
          html: renderReviewAdminEmail(emailData),
        },
      ]).catch((e: unknown) => console.error("review submit emails failed", e));
    }

    return { ok: true, status: "pending" };
  });

export const markReviewHelpful = createServerFn({ method: "POST" })
  .inputValidator((input: { reviewId: string }) =>
    z.object({ reviewId: z.string().uuid() }).parse(input),
  )
  .handler(async ({ data }): Promise<{ ok: boolean; helpful_count?: number }> => {
    const ip = clientIp();
    const allowed = await reviewRateLimit(ip, { max: 30, windowMinutes: 60 });
    if (!allowed) return { ok: false };
    const { data: current } = await localAdmin
      .from("product_reviews")
      .select("helpful_count")
      .eq("id", data.reviewId)
      .eq("status", "approved")
      .maybeSingle();
    const cur = (current as { helpful_count?: number } | null)?.helpful_count;
    if (cur == null) return { ok: false };
    const next = cur + 1;
    const { error } = await localAdmin
      .from("product_reviews")
      .update({ helpful_count: next })
      .eq("id", data.reviewId);
    if (error) {
      console.error("markReviewHelpful failed", error);
      return { ok: false };
    }
    return { ok: true, helpful_count: next };
  });